import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, IdempotentButton, Label } from '@flexibilitypay/ui';

import { ComplianceApiError } from '../../api/compliance.api';

const MIN_REASON_LENGTH = 5;
const MAX_REASON_LENGTH = 2000;

export type ComplianceDialogMode = 'block' | 'unblock';

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T060
 * @see functional/Specs/specs_cycle-2.md HU-11-FE
 */
export interface ComplianceActionDialogProps {
  open: boolean;
  mode: ComplianceDialogMode;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}

export function ComplianceActionDialog({
  open,
  mode,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: ComplianceActionDialogProps) {
  const { t } = useTranslation(['compliance', 'common']);
  const ref = useRef<HTMLDialogElement | null>(null);
  const [reason, setReason] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setReason('');
      setShowValidation(false);
    }
  }

  const trimmed = reason.trim();
  const isReasonValid = trimmed.length >= MIN_REASON_LENGTH;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isReasonValid) {
      setShowValidation(true);
      return;
    }
    onSubmit(trimmed);
  }

  const titleKey =
    mode === 'block' ? 'compliance:dialog.block.title' : 'compliance:dialog.unblock.title';
  const descriptionKey =
    mode === 'block'
      ? 'compliance:dialog.block.description'
      : 'compliance:dialog.unblock.description';

  return (
    <dialog
      ref={ref}
      data-testid="compliance-dialog"
      className="backdrop:bg-background/80 fixed top-1/2 left-1/2 m-0 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md rounded-lg border border-border bg-background p-0 text-foreground shadow-lg"
      onClick={(e) => {
        if (e.target === ref.current && !submitting) onClose();
      }}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-base font-semibold">{t(titleKey)}</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          disabled={submitting}
          aria-label={t('compliance:dialog.cancel')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
        <p className="text-sm text-muted-foreground">{t(descriptionKey)}</p>
        <div className="flex flex-col gap-2">
          <Label htmlFor="compliance-reason">
            {t('compliance:dialog.reason.label')}
          </Label>
          <textarea
            id="compliance-reason"
            data-testid="compliance-reason"
            required
            rows={4}
            maxLength={MAX_REASON_LENGTH}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t('compliance:dialog.reason.placeholder')}
            aria-invalid={showValidation && !isReasonValid ? true : undefined}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
          />
          {showValidation && !isReasonValid && (
            <p role="alert" className="text-xs text-destructive">
              {t('compliance:dialog.reason.minLength')}
            </p>
          )}
        </div>
        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            {t('compliance:dialog.cancel')}
          </Button>
          <IdempotentButton
            type="submit"
            variant={mode === 'block' ? 'destructive' : 'default'}
            isPending={submitting}
            disabled={!isReasonValid}
            pendingLabel={t('compliance:dialog.submitting')}
          >
            {t('compliance:dialog.confirm')}
          </IdempotentButton>
        </div>
      </form>
    </dialog>
  );
}

export function resolveComplianceErrorMessage(
  error: unknown,
  t: (key: string) => string,
): string {
  if (error instanceof ComplianceApiError) {
    switch (error.code) {
      case 'COMPLIANCE_FORBIDDEN':
        return t('compliance:errors.forbidden');
      case 'ACCOUNT_NOT_FOUND':
        return t('compliance:errors.notFound');
      case 'COMPLIANCE_ALREADY_IN_STATE':
        return t('compliance:errors.alreadyInState');
      case 'COMPLIANCE_INVALID_TRANSITION':
        return t('compliance:errors.invalidTransition');
      case 'COMPLIANCE_UPSTREAM_ERROR':
        return t('compliance:errors.upstream');
      default:
        return t('compliance:errors.generic');
    }
  }
  return t('compliance:errors.generic');
}
